import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  History,
  ChevronDown,
  ChevronUp,
  Mail,
  Coins,
  Info,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { 
  faucetPayApi, 
  FaucetPayPublicConfig, 
  FaucetPayCoinConfig,
  FaucetPayWithdrawalHistory as FaucetPayWithdrawalHistoryType,
  FaucetPayCooldownInfo,
  FaucetPayConversionPreview,
} from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useTelegram } from '../../hooks';
import toast from 'react-hot-toast';

type WithdrawalStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';

// Coin icons/colors
const coinColors: Record<string, string> = {
  BTC: 'bg-orange-500',
  ETH: 'bg-blue-500',
  DOGE: 'bg-yellow-500',
  LTC: 'bg-gray-400',
  TRX: 'bg-red-500',
  USDT: 'bg-green-500',
  TON: 'bg-blue-400',
  SOL: 'bg-purple-500',
  BNB: 'bg-yellow-400',
  XRP: 'bg-gray-500',
};

export function FaucetPayWithdrawal() {
  // State
  const [config, setConfig] = useState<FaucetPayPublicConfig | null>(null);
  const [email, setEmail] = useState('');
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [history, setHistory] = useState<FaucetPayWithdrawalHistoryType[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCoinSelector, setShowCoinSelector] = useState(false);
  const [cooldownInfo, setCooldownInfo] = useState<FaucetPayCooldownInfo | null>(null);
  const [conversionPreview, setConversionPreview] = useState<FaucetPayConversionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const user = useAppStore((state) => state.user);
  const updateBalance = useAppStore((state) => state.updateBalance);
  const { hapticFeedback, hapticNotification } = useTelegram();

  // Get selected coin config
  const selectedCoinConfig = useMemo((): FaucetPayCoinConfig | null => {
    if (!config || !selectedCoin) return null;
    return config.coins.find(c => c.coin === selectedCoin) || null;
  }, [config, selectedCoin]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
    loadHistory();
    checkCooldown();
  }, []);

  // Debounced preview fetch
  useEffect(() => {
    const numAmount = parseInt(amount) || 0;
    const minWithdrawal = selectedCoinConfig?.minWithdrawal || 0;
    if (!selectedCoin || numAmount < minWithdrawal) {
      setConversionPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const preview = await faucetPayApi.previewConversion(selectedCoin, numAmount);
        setConversionPreview(preview);
      } catch (error) {
        console.error('[FaucetPay] Preview error:', error);
        setConversionPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [selectedCoin, amount, selectedCoinConfig?.minWithdrawal]);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const data = await faucetPayApi.getConfig();
      setConfig(data);
      
      // Set default selected coin to first enabled coin
      if (data.coins && data.coins.length > 0) {
        const firstEnabled = data.coins.find(c => c.enabled);
        if (firstEnabled) {
          setSelectedCoin(firstEnabled.coin);
        }
      }
    } catch (error) {
      console.error('[FaucetPay] Failed to load config:', error);
      toast.error('Failed to load FaucetPay configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await faucetPayApi.getHistory();
      setHistory(data.withdrawals || []);
    } catch (error) {
      console.error('[FaucetPay] Failed to load history:', error);
    }
  };

  const checkCooldown = async () => {
    try {
      const data = await faucetPayApi.checkCooldown();
      setCooldownInfo(data);
    } catch (error) {
      console.error('[FaucetPay] Failed to check cooldown:', error);
    }
  };

  // Use API preview for accurate conversion
  const estimate = useMemo(() => {
    if (!conversionPreview) return null;
    
    return {
      gross: conversionPreview.coinAmount,
      fee: conversionPreview.feeAmount,
      net: conversionPreview.netAmount,
      feePercent: conversionPreview.feePercent,
      usdEquivalent: conversionPreview.usdEquivalent,
      coinRate: conversionPreview.coinRate,
      priceSource: conversionPreview.priceSource,
      // Rates from admin withdrawal settings
      pointsToTonRate: conversionPreview.pointsToTonRate,
      tonUsdRate: conversionPreview.tonUsdRate,
    };
  }, [conversionPreview]);

  // Validate email format
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle withdrawal
  const handleWithdraw = async () => {
    if (!selectedCoin || !email || !amount) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!selectedCoinConfig) {
      toast.error('Invalid coin selected');
      return;
    }

    const numAmount = parseInt(amount);
    if (numAmount < selectedCoinConfig.minWithdrawal) {
      toast.error(`Minimum withdrawal is ${selectedCoinConfig.minWithdrawal.toLocaleString()} points`);
      return;
    }

    if (selectedCoinConfig.maxWithdrawal && numAmount > selectedCoinConfig.maxWithdrawal) {
      toast.error(`Maximum withdrawal is ${selectedCoinConfig.maxWithdrawal.toLocaleString()} points`);
      return;
    }

    if (user && numAmount > user.balance) {
      toast.error('Insufficient balance');
      return;
    }

    if (cooldownInfo && !cooldownInfo.canWithdraw) {
      toast.error('Please wait for cooldown to complete');
      return;
    }

    setLoading(true);
    hapticFeedback('medium');

    try {
      const result = await faucetPayApi.requestWithdrawal({
        faucetPayEmail: email,
        coin: selectedCoin,
        pointsAmount: numAmount,
      });

      if (result.success) {
        hapticNotification('success');
        toast.success(`Withdrawal request submitted! ${result.netAmount?.toFixed(8)} ${selectedCoin}`);
        
        // Update balance
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        }

        // Reset form
        setAmount('');
        
        // Refresh history and cooldown
        loadHistory();
        checkCooldown();
      }
    } catch (error: any) {
      hapticNotification('error');
      toast.error(error.response?.data?.error || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  // Render status badge
  const renderStatus = (status: WithdrawalStatus) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock, text: 'Pending' },
      processing: { color: 'bg-blue-500/20 text-blue-400', icon: Loader2, text: 'Processing' },
      completed: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle, text: 'Completed' },
      failed: { color: 'bg-red-500/20 text-red-400', icon: XCircle, text: 'Failed' },
      refunded: { color: 'bg-purple-500/20 text-purple-400', icon: RefreshCw, text: 'Refunded' },
    };
    
    const cfg = statusConfig[status];
    const Icon = cfg.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${cfg.color}`}>
        <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {cfg.text}
      </span>
    );
  };

  // Loading state
  if (configLoading) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Disabled state
  if (!config?.enabled || config?.emergencyStop) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">FaucetPay Withdrawal</h2>
            <p className="text-sm text-gray-400">Instant crypto payments</p>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
          <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
          <p className="text-yellow-400 font-medium">
            {config?.emergencyStop 
              ? 'Withdrawals are temporarily paused'
              : 'FaucetPay withdrawals are currently unavailable'}
          </p>
          <p className="text-gray-400 text-sm mt-1">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">FaucetPay</h2>
            <p className="text-sm text-gray-400">{config.processingTime}</p>
          </div>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
        >
          <History className="w-5 h-5 text-gray-300" />
        </button>
      </div>

      {/* Description */}
      {config.description && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 mb-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-400 mt-0.5" />
            <p className="text-sm text-blue-300">{config.description}</p>
          </div>
        </div>
      )}

      {/* Cooldown / Daily Limit Warning */}
      {cooldownInfo && !cooldownInfo.canWithdraw && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <p className="text-sm text-yellow-300">
              {cooldownInfo.cooldownRemaining
                ? `Cooldown: ${Math.ceil(cooldownInfo.cooldownRemaining / 60)} minutes remaining`
                : 'Daily limit reached. Try again tomorrow.'}
            </p>
          </div>
        </div>
      )}

      {/* Main Form */}
      <AnimatePresence mode="wait">
        {!showHistory ? (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* FaucetPay Email */}
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">
                <Mail className="w-4 h-4 inline mr-1" />
                FaucetPay Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your FaucetPay email"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
              />
            </div>

            {/* Coin Selector */}
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">
                <Coins className="w-4 h-4 inline mr-1" />
                Select Cryptocurrency
              </label>
              <button
                onClick={() => setShowCoinSelector(!showCoinSelector)}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white flex items-center justify-between hover:border-gray-500 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {selectedCoin && (
                    <div className={`w-6 h-6 rounded-full ${coinColors[selectedCoin] || 'bg-gray-500'} flex items-center justify-center`}>
                      <span className="text-xs font-bold text-white">
                        {selectedCoin.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span>{selectedCoin || 'Select a coin'}</span>
                </div>
                {showCoinSelector ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>

              {/* Coin Dropdown */}
              <AnimatePresence>
                {showCoinSelector && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-2 bg-gray-700 rounded-xl border border-gray-600 overflow-hidden max-h-48 overflow-y-auto"
                  >
                    {config.coins.filter(c => c.enabled).map((coinConfig) => (
                      <button
                        key={coinConfig.coin}
                        onClick={() => {
                          setSelectedCoin(coinConfig.coin);
                          setShowCoinSelector(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-600 transition-colors ${
                          selectedCoin === coinConfig.coin ? 'bg-gray-600' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full ${coinColors[coinConfig.coin] || 'bg-gray-500'} flex items-center justify-center`}>
                            <span className="text-xs font-bold text-white">
                              {coinConfig.coin.charAt(0)}
                            </span>
                          </div>
                          <span className="text-white">{coinConfig.coin}</span>
                        </div>
                        <span className="text-gray-400 text-sm">
                          Min: {coinConfig.minWithdrawal.toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">
                Withdrawal Amount (Points)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Min: ${selectedCoinConfig?.minWithdrawal?.toLocaleString() || '10,000'}`}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                />
                <button
                  onClick={() => user && setAmount(String(user.balance))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400 text-sm hover:text-green-300"
                >
                  MAX
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-1">
                Available: {user?.balance?.toLocaleString() || 0} points
              </p>
            </div>

            {/* Estimate */}
            {previewLoading && (
              <div className="bg-gray-700/50 rounded-xl p-4 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-green-500 animate-spin mr-2" />
                <span className="text-gray-400">Calculating...</span>
              </div>
            )}
            {estimate && !previewLoading && (
              <div className="bg-gray-700/50 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Estimated Amount</span>
                  <span className="text-white font-medium">
                    ~{estimate.gross.toFixed(8)} {selectedCoin}
                  </span>
                </div>
                {estimate.usdEquivalent && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">USD Value</span>
                    <span className="text-gray-300">
                      ~${estimate.usdEquivalent.toFixed(4)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Fee ({estimate.feePercent}%)</span>
                  <span className="text-red-400">
                    -{estimate.fee.toFixed(8)} {selectedCoin}
                  </span>
                </div>
                <div className="border-t border-gray-600 pt-2 flex justify-between items-center">
                  <span className="text-gray-300 font-medium">You'll Receive</span>
                  <span className="text-green-400 font-bold">
                    ~{estimate.net.toFixed(8)} {selectedCoin}
                  </span>
                </div>
                {/* Price Source Info */}
                <div className="mt-2 pt-2 border-t border-gray-600/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Rate: 1 {selectedCoin} = ${estimate.coinRate?.toLocaleString()}
                    </span>
                    <span className="text-gray-500">
                      via {estimate.priceSource === 'coingecko' ? 'CoinGecko' : 
                           estimate.priceSource === 'admin_override' ? 'Fixed' :
                           estimate.priceSource === 'system_config' ? 'System' : 
                           estimate.priceSource}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleWithdraw}
              disabled={loading || !email || !selectedCoin || !amount || (cooldownInfo !== null && !cooldownInfo.canWithdraw)}
              className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                loading || !email || !selectedCoin || !amount || (cooldownInfo !== null && !cooldownInfo.canWithdraw)
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpRight className="w-5 h-5" />
                  Withdraw to FaucetPay
                </>
              )}
            </motion.button>
          </motion.div>
        ) : (
          /* History View */
          <motion.div
            key="history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Withdrawal History</h3>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-400 hover:text-white text-sm"
              >
                Back
              </button>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No withdrawal history yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {history.map((item) => (
                  <div
                    key={item._id}
                    className="bg-gray-700/50 rounded-xl p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full ${coinColors[item.coin] || 'bg-gray-500'} flex items-center justify-center`}>
                          <span className="text-xs font-bold text-white">
                            {item.coin.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {item.netAmount.toFixed(8)} {item.coin}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {item.pointsAmount.toLocaleString()} points
                          </p>
                        </div>
                      </div>
                      {renderStatus(item.status)}
                    </div>
                    <div className="text-xs text-gray-500 flex justify-between">
                      <span>{new Date(item.createdAt).toLocaleString()}</span>
                      {item.faucetPayPayoutId && (
                        <span className="font-mono truncate max-w-[100px]">
                          ID: {item.faucetPayPayoutId.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                    {item.errorMessage && (
                      <p className="text-red-400 text-xs mt-1">{item.errorMessage}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FaucetPayWithdrawal;
